import { createActions, createReducer } from 'reduxsauce';
import { cloneDeep, isEmpty, forEach } from 'lodash';
import utils from '@utils';
import User from '@components/common/User';

export { default as thunks } from './thunks';

/* ------------- Initial State ------------- */
export const INITIAL_STATE = {
	isInitiated      : false,
	isAuthenticated  : false,
	isProcessingMsal : false,
	loading          : false,
	user             : {
		TimeZoneId : 'America/Chicago', // Set default timeZoneId
	},
	USStates  : [],
	timeZones : [],
	loaders   : {
		USStates         : false,
		addPaymentMethod : false,
	},
	loginData : {
		liveValidate   : false,
		progressButton : '',
		credentials    : {
			username : '',
			password : '',
		},
	},
	forgotPassword : {
		email      : '',
		validation : null,
		loading    : false,
	},
	personal : {
		isEdit      : false,
		errors      : {},
		buttonState : '',
	},
	profile : {
		isEdit      : false,
		errors      : {},
		buttonState : '',
		loading     : false,
	},
	changePassword : {
		password        : '',
		newPassword     : '',
		confirmPassword : '',
		errors          : {},
		buttonState     : '',
	},
	bio : {
		isEdit      : false,
		biography   : '',
		buttonState : '',
	},
	payment : {
		loading : false,
		data    : {},
	},
	addPayment : {
		showAddPayment : false,
	},
	bankAccountVerification : {
		show          : false,
		bankAccountId : '',
		amountOne     : '',
		amountTwo     : '',
	},
	notification : {
		settings : [],
		loading  : false,
		clockIn  : {
			sms   : false,
			email : false,
		},
		clockOut : {
			sms   : false,
			email : false,
		},
		newLocation : {
			sms   : false,
			email : false,
		},
		pendingPayments : {
			sms   : false,
			email : false,
		},
	},
	resetPasswordConfirmed : false,
	subName                : '',

};

/* ------------- Types and Action Creators ------------- */
export const { Types, Creators } = createActions({
	init                         : null,
	login                        : null,
	logout                       : null,
	setLoading                   : ['name', 'value'],
	setLiveValidate              : ['liveValidate'],
	receiveUser                  : ['user'],
	setCredentials               : ['credentials'],
	updateStates                 : ['data'],
	updateTimeZones              : ['data'],
	setFormField                 : ['form', 'property', 'value'],
	setFormErrors                : ['form', 'errors'],
	resetForm                    : ['form'],
	setEditMode                  : ['form', 'value'],
	changeForgotPasswordData     : ['propertyObject'],
	changeResetPasswordConfirmed : ['value'],
	updateActiveLink             : ['name', 'value'],
	updateSubName                : ['value'],
	updateNotificationSettings   : ['value'],
	setIsProcessingMsal          : ['value'],
}, { prefix : '@app/' });

/* ------------- Hookup Reducers To Types ------------- */
export const reducer = createReducer(INITIAL_STATE, {
	[Types.SET_IS_PROCESSING_MSAL] : (state, { value }) => ({
		...state,
		isProcessingMsal : value,
	}),

	[Types.INIT] : (state) => ({
		...state,
		isInitiated : true,
	}),

	[Types.LOGIN] : (state) => ({
		...state,
		isAuthenticated : true,
		loginData       : INITIAL_STATE.loginData,
	}),

	[Types.LOGOUT] : (state) => ({
		...INITIAL_STATE,
		isInitiated : state.isInitiated,
	}),

	[Types.SET_LOADING] : (state, { name, value }) => ({
		...state,
		loaders : {
			...state.loaders,
			[name] : value,
		},
	}),

	[Types.RECEIVE_USER] : (state, { user }) => ({
		...state,
		user : new User(user),
	}),

	[Types.SET_CREDENTIALS] : (state, { credentials }) => ({
		...state,
		loginData : {
			...state.loginData,
			credentials : {
				...state.loginData.credentials,
				...credentials,
			},
		},
	}),

	[Types.SET_LIVE_VALIDATE] : (state, { liveValidate }) => ({
		...state,
		loginData : {
			...state.loginData,
			liveValidate,
		},
	}),

	[Types.UPDATE_STATES] : (state, { data }) => ({
		...state,
		USStates : data,
	}),

	[Types.UPDATE_TIME_ZONES] : (state, { data }) => ({
		...state,
		timeZones : data,
	}),

	[Types.SET_FORM_FIELD] : (state, { form, property, value }) => {
		const newForm = cloneDeep(state[form]);

		newForm[property] = value;
		if (newForm.errors && newForm.errors[property]) delete newForm.errors[property];

		return ({
			...state,
			[form] : {
				...newForm,
			},
		});
	},
	[Types.RESET_FORM] : (state, { form }) => ({
		...state,
		[form] : INITIAL_STATE[form],
	}),

	[Types.SET_FORM_ERRORS] : (state, { form, errors }) => ({
		...state,
		[form] : {
			...state[form],
			errors,
		},
	}),

	[Types.SET_EDIT_MODE] : (state, { form, value }) => ({
		...state,
		[form] : {
			...state[form],
			isEdit : value,
		},
	}),

	[Types.CHANGE_FORGOT_PASSWORD_DATA] : (state, { propertyObject = {} }) => {
		const newState = cloneDeep(state);
		if (isEmpty(propertyObject)) newState.forgotPassword = INITIAL_STATE.forgotPassword;
		else {
			forEach(propertyObject, (item, key) => {
				newState.forgotPassword[key] = item;
			});
		}
		return newState;
	},

	[Types.CHANGE_RESET_PASSWORD_CONFIRMED] : (state, { value }) => ({
		...state,
		resetPasswordConfirmed : value,
	}),

	[Types.UPDATE_ACTIVE_LINK] : (state, { name, value }) => (
		utils.updateStateGeneric({ state, property : ['active', name], value })
	),

	[Types.UPDATE_SUB_NAME] : (state, { value }) => (
		utils.updateStateGeneric({ state, property : 'subName', value })
	),

	[Types.UPDATE_NOTIFICATION_SETTINGS] : (state, { value }) => (
		utils.updateStateGeneric({ state, property : ['notification', 'settings'], value })
	),
});

export default Creators;
